import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {CHARTS_COLORS, COLORS} from '@scope/scope-one-ui/theme';
import {Widget, LineChart, Box, Typography, StatusBadge, Divider, ChartLegend} from '@scope/scope-one-ui/core';
import { SectionA } from '../ESGSentiment.styled';
import { GeneralInformationWidget } from '../GeneralInformationWidget';
import ImpactReviewWidget from '../ImpactReviewWidget/ImpactReviewWidget';
import { useGetHistoryChart } from '../../services/esgSentiment';
import { EsgSentimentParams } from '../../types/esgSentiment';
import { ChartLoader, DailyScoreHeaderLoader } from './ESGDailyScoreSection.loader';

export const ESGDailyScoreSection: FC = () => {
  const { t } = useTranslation();
  const { entityId } = useParams<EsgSentimentParams>();
  const { data, loading } = useGetHistoryChart(entityId!);

  const normalize = (attentionInPercent: number): number => {
    if (attentionInPercent >=200) return 10.0
    if (attentionInPercent <=-100) return 0;
    return 0.1/3 * attentionInPercent + 10/3
  }

  const historyData = [
    {
      name: 'ESG Sentiment Score',
      color: CHARTS_COLORS[0],
      data: (data?.scoreForGraph || [])
        .map(({ monthShortName, value }) => ({ normalizedY: value,y: value, x: monthShortName }))
        .reverse(),
    },
    {
      name: 'Attention Buzz',
      color: CHARTS_COLORS[15],
      data: (data?.scoreForGraph || [])
        .map(({ monthShortName, attentionBuzz }) => ({ normalizedY: normalize(attentionBuzz * 100), y: attentionBuzz*100 , x: monthShortName }))
        .reverse(),
    }
  ];

  const legend = (
    <ChartLegend items={[{...historyData[0], label: 'ESG Sentiment Score'}, {...historyData[1], label: 'Attention Buzz'} ]} />
  );

  return (
    <SectionA>
      <ImpactReviewWidget esgScore={data?.esgScore} loading={loading} />
      <Widget title={t('History')} loading={loading}>
        {loading && <DailyScoreHeaderLoader />}
        {!loading && (
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <Typography variant="subtitle" color={COLORS.typography.label}>
              {t('Latest change')}
            </Typography>
            <Typography variant="subtitle" color={COLORS.typography.label}>
              {data?.latestChange}
            </Typography>
            <StatusBadge status={data?.status || 'STABLE'} />
          </Box>
        )}
        <Box px={2} pb={2} display="flex" flexDirection="column" gridGap={2}>
          <Divider />
          {loading && <ChartLoader />}
          {!loading && (<>
            <LineChart
              data={historyData}
              chartHeight={38}
              hideYAxis
              yDataType={['text','percentage']}
              yDomain={[0, 11]}
              tickCount={11}
              dots="regular"
              xLabel="Month"
              label="Value"
            />
          {legend}
            </>
          )}
        </Box>
      </Widget>
      <GeneralInformationWidget />
    </SectionA>
  );
};
